<template>
  <div class="min-h-screen bg-gray-100 py-10 px-4">
    <div class="max-w-3xl mx-auto">
      <div class="text-center mb-8">
        <h1 class="text-3xl font-bold text-blue-900">ALLDOQ</h1>
      </div>

      <div v-if="loading" class="text-center text-gray-500 py-20">Loading…</div>

      <div v-else-if="loadError" class="bg-white rounded-lg shadow p-8 text-center">
        <p class="text-red-600 font-medium">This welcome link is invalid or has expired.</p>
        <p class="text-gray-500 text-sm mt-2">Please contact your administrator for a new invitation.</p>
      </div>

      <template v-else>
        <!-- Greeting -->
        <div class="bg-white rounded-lg shadow p-8 mb-6">
          <h2 class="text-2xl font-semibold text-gray-900">
            Welcome{{ invite && invite.app ? ' to ' + invite.app.name : '' }}
          </h2>
          <p class="text-gray-600 mt-2">
            <template v-if="invite && invite.email">You've been invited as <strong>{{ invite.email }}</strong>. </template>
            Before you get started, please review the latest terms, policies and documents below.
          </p>
        </div>

        <!-- Per-app document catalogue -->
        <div v-for="app in apps" :key="app.id" class="bg-white rounded-lg shadow mb-6 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-100 bg-gray-50">
            <h3 class="font-semibold text-gray-900">{{ app.name }}</h3>
            <p v-if="app.description" class="text-sm text-gray-500 mt-0.5">{{ app.description }}</p>
          </div>

          <div v-if="app.documents.length === 0" class="px-6 py-6 text-sm text-gray-400">
            No documents published yet.
          </div>

          <div v-else>
            <div v-for="(group, cat) in grouped(app.documents)" :key="cat" class="px-6 py-4 border-b border-gray-50 last:border-0">
              <p class="text-xs uppercase tracking-wide text-gray-400 mb-2">{{ label(cat) }}</p>
              <ul class="space-y-2">
                <li v-for="doc in group" :key="doc.id" class="flex items-center justify-between">
                  <div>
                    <a :href="viewUrl(doc)" target="_blank" rel="noopener" class="text-blue-700 hover:underline font-medium">{{ doc.title }}</a>
                    <span v-if="doc.version" class="text-xs text-gray-400 ml-2">{{ doc.version }}</span>
                  </div>
                  <div class="flex items-center gap-3 text-sm">
                    <a :href="viewUrl(doc)" target="_blank" rel="noopener" class="text-blue-700 hover:underline">View</a>
                    <a v-if="doc.download_path" :href="downloadUrl(doc)" class="text-gray-500 hover:underline">Download</a>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <p class="text-center text-xs text-gray-400 mt-8">
          These are the current published versions, managed centrally in ALLDOQ Ground Control.
        </p>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import onboardingService from '../utils/onboardingService'

const route = useRoute()
const loading = ref(true)
const loadError = ref(false)
const invite = ref(null)
const apps = ref([])

const ORDER = ['terms', 'policy', 'document', 'manual', 'guide']

function label (c) { return c.charAt(0).toUpperCase() + c.slice(1) + (c === 'policy' ? ' policies' : 's') }
function viewUrl (doc) { return onboardingService.documentUrl(doc) }
function downloadUrl (doc) { return onboardingService.documentUrl(doc, { download: true }) }

function grouped (docs) {
  const out = {}
  ORDER.forEach(c => {
    const items = docs.filter(d => d.category === c)
    if (items.length) out[c] = items
  })
  return out
}

onMounted(async () => {
  try {
    const token = route.params.token
    if (token) {
      const { data } = await onboardingService.getWelcome(token)
      invite.value = data.invite
      apps.value = data.apps
    } else {
      const { data } = await onboardingService.getApps()
      apps.value = data.apps
    }
  } catch (e) {
    loadError.value = true
  } finally {
    loading.value = false
  }
})
</script>
